import { useContext, type ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  formatElapsedDuration,
  formatRelativeTime,
  formatRelativeTimeFuzzy,
  formatDateTimeParts,
} from '@/lib/date-utils';
import {
  TimestampFormatContext,
  type TimestampFormat,
} from '@/contexts/TimestampFormatContext';

/**
 * Which relative-time formatter the inline/tooltip relative form uses:
 * - `minutes` (default): minute precision via {@link formatRelativeTime}.
 * - `fuzzy`: lossy "less than …" ladder via {@link formatRelativeTimeFuzzy},
 *   for screens whose data is not accurate to the minute.
 * - `duration`: elapsed time without the "ago" suffix via
 *   {@link formatElapsedDuration}, for columns that show a length of time
 *   (e.g. "Running For").
 * Only meaningful when the effective format is `relative`.
 */
type RelativeTimeVariant = 'minutes' | 'fuzzy' | 'duration';

interface RelativeTimeWithTooltipProps {
  date: Date | string;
  /**
   * `relative` shows a humanized delta; `absolute` shows the fixed zone-labelled
   * timestamp. When omitted, the effective format comes from
   * `TimestampFormatContext` (the per-user Timestamp Format setting); an
   * explicit prop always wins as an override.
   */
  format?: TimestampFormat;
  /**
   * Selects the relative-time formatter. Defaults to `minutes` (exact minute
   * precision). Pass `fuzzy` on screens whose timestamps are not accurate to
   * the minute so they never show an exact minute count below the hour, or
   * `duration` for a length-of-time column ("about 3 hours", no "ago").
   */
  variant?: RelativeTimeVariant;
  /**
   * Render the trigger as an inline `<span>` instead of the default block-level
   * flex `<div>`, so it nests validly inside an inline context (e.g. an inline
   * metadata chip whose parent is itself a `<span>`).
   */
  inline?: boolean;
}

/**
 * Displays a timestamp with a hover tooltip. The inline form follows the
 * effective format (explicit prop > per-user context setting > relative
 * default). Relative mode: relative inline + absolute time on hover. Absolute
 * mode: absolute time inline + the full absolute time and relative ("ago") on
 * hover. An absolute value takes at most two lines (date, then time),
 * truncating rather than wrapping further, so date columns can stay narrow;
 * the tooltip keeps the full value.
 */
export function RelativeTimeWithTooltip({
  date,
  format,
  variant = 'minutes',
  inline = false,
}: RelativeTimeWithTooltipProps): ReactElement {
  const { value: contextFormat } = useContext(TimestampFormatContext);
  const effectiveFormat = format ?? contextFormat;
  const timeZone = useDisplayTimeZone();

  // Pass the raw value (not a locally pre-parsed `new Date(date)`) to the
  // formatters: they read timezone-less backend strings as UTC, so a
  // pre-parse here would re-introduce the local-offset shift in both the
  // inline relative text and the absolute tooltip.
  const relative =
    variant === 'fuzzy'
      ? formatRelativeTimeFuzzy(date)
      : variant === 'duration'
        ? formatElapsedDuration(date)
        : formatRelativeTime(date);
  const parts = formatDateTimeParts(date, timeZone);

  // `inline` swaps the trigger container to a <span> so it nests validly inside
  // inline contexts (a <div> inside a <span> chip is invalid HTML).
  const Container = inline ? 'span' : 'div';
  const containerClass = inline ? 'inline-flex min-w-0' : 'flex min-w-0';

  // A value no formatter can read is a dash in either format, with nothing to
  // hover (never the relative formatter's "invalid").
  if (!parts) {
    return (
      <Container className={containerClass}>
        <span data-testid="relative-time-value">—</span>
      </Container>
    );
  }

  // Same string `formatDateTime` builds, without formatting twice.
  const absolute = `${parts.date} ${parts.time}`;
  const isAbsolute = effectiveFormat === 'absolute';
  const tooltipText = isAbsolute ? `${absolute} (${relative})` : absolute;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Container className={containerClass}>
            {isAbsolute && !inline ? (
              // Date and time each stay on one line: side by side when there
              // is room, time below when not, and an ellipsis rather than a
              // third line. The space keeps the text "date time" for copy and
              // screen readers; flex doesn't render it.
              <span
                data-testid="relative-time-value"
                className="flex min-w-0 flex-wrap gap-x-1"
              >
                <span className="max-w-full truncate">{parts.date}</span>{' '}
                <span className="max-w-full truncate">{parts.time}</span>
              </span>
            ) : (
              // Relative wraps (table cells are nowrap by default) so date
              // columns stay narrow. Inline form, used inside chips, truncates
              // so it never grows a second line; the tooltip has the rest.
              <span
                data-testid="relative-time-value"
                className={
                  inline ? 'min-w-0 truncate' : 'min-w-0 whitespace-normal'
                }
              >
                {isAbsolute ? absolute : relative}
              </span>
            )}
          </Container>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
